import React from 'react';

const MeetAndGreetSchema = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://airportparkingdeals.co.uk/#organization",
        "name": "Airport Parking Deals",
        "url": "https://airportparkingdeals.co.uk",
        "logo": {
          "@type": "ImageObject",
          "url": "https://airportparkingdeals.co.uk/logo.png"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+44-800-000-0000",
          "contactType": "Customer Service",
          "areaServed": "GB",
          "availableLanguage": "English"
        },
        "sameAs": [
          "https://www.facebook.com/airportparkingdeals",
          "https://twitter.com/airportparking"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://airportparkingdeals.co.uk/#website",
        "url": "https://airportparkingdeals.co.uk",
        "name": "Airport Parking Deals",
        "publisher": {
          "@id": "https://airportparkingdeals.co.uk/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://airportparkingdeals.co.uk/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://airportparkingdeals.co.uk/meet-and-greet-parking#webpage",
        "url": "https://airportparkingdeals.co.uk/meet-and-greet-parking",
        "name": "Meet & Greet Airport Parking | How It Works & Best Time to Book | Airport Parking Deals",
        "isPartOf": {
          "@id": "https://airportparkingdeals.co.uk/#website"
        },
        "about": {
          "@id": "https://airportparkingdeals.co.uk/meet-and-greet-parking#service"
        },
        "description": "Discover stress-free Meet & Greet airport parking. Learn how it works in 3 simple steps, when to book for the best price, and compare trusted providers today.",
        "breadcrumb": {
          "@id": "https://airportparkingdeals.co.uk/meet-and-greet-parking#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://airportparkingdeals.co.uk/meet-and-greet-parking#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://airportparkingdeals.co.uk"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Meet & Greet Parking",
            "item": "https://airportparkingdeals.co.uk/meet-and-greet-parking"
          }
        ]
      },
      {
        "@type": "Service",
        "@id": "https://airportparkingdeals.co.uk/meet-and-greet-parking#service",
        "serviceType": "Meet & Greet Airport Parking",
        "name": "Meet & Greet Airport Parking Services",
        "description": "Premium Meet & Greet airport parking with professional chauffeur service. Drive to the terminal, hand over your keys, and walk straight to check-in. VIP treatment for stress-free travel.",
        "provider": {
          "@id": "https://airportparkingdeals.co.uk/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Meet & Greet Parking Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Terminal Drop-Off Service",
                "description": "Drive directly to your terminal - professional chauffeur meets you at the entrance"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Secure Vehicle Storage",
                "description": "Your car stored in secure, off-airport compound with CCTV monitoring"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Return Collection",
                "description": "Your car delivered back to the terminal on your return"
              }
            }
          ]
        },
        "termsOfService": "https://airportparkingdeals.co.uk/terms-and-conditions",
        "audience": {
          "@type": "Audience",
          "audienceType": "Travelers seeking premium convenience"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How does Meet & Greet parking work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Meet & Greet parking works in 3 simple steps: 1) Drive to the terminal and call the driver when you arrive, 2) A uniformed chauffeur meets you, inspects your vehicle, and takes your keys, 3) On return, call the driver and your car is delivered back to the terminal."
            }
          },
          {
            "@type": "Question",
            "name": "When is the best time to book Meet & Greet parking?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Book several weeks or months in advance for the best prices. Meet & Greet is a premium service with limited capacity, and early booking ensures guaranteed availability, especially during peak seasons like school holidays."
            }
          },
          {
            "@type": "Question",
            "name": "Is Meet & Greet parking secure?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, all Meet & Greet services use fully insured, professional drivers. Your vehicle is stored in secure, off-airport compounds with CCTV monitoring and regular patrols for complete peace of mind."
            }
          },
          {
            "@type": "Question",
            "name": "What are the benefits of Meet & Greet parking?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Meet & Greet offers ultimate convenience - no parking stress, no shuttle buses, no long walks. It's time-saving, perfect for families with luggage, and provides a VIP start to your journey."
            }
          }
        ]
      },
      {
        "@type": "HowTo",
        "name": "How to Use Meet & Greet Airport Parking",
        "description": "Step-by-step guide to using Meet & Greet airport parking service",
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "Drive & Call",
            "text": "Drive directly to the airport's terminal departures. Once you're a few minutes away, call the dedicated number provided in your booking confirmation to alert the driver.",
            "image": "https://airportparkingdeals.co.uk/images/drive-and-call.jpg"
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "Meet & Handover",
            "text": "A uniformed chauffeur meets you outside the terminal entrance. They perform a quick vehicle inspection, hand you a report, and take your keys. Walk straight to check-in.",
            "image": "https://airportparkingdeals.co.uk/images/meet-and-handover.jpg"
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "Relax & Reclaim",
            "text": "Your car is stored in a secure compound. On return, call the same number after collecting luggage. Your car will be waiting at the terminal for a swift getaway.",
            "image": "https://airportparkingdeals.co.uk/images/relax-and-reclaim.jpg"
          }
        ]
      },
      {
        "@type": "LocalBusiness",
        "name": "Airport Parking Deals - Meet & Greet Service",
        "description": "Premium meet and greet airport parking services across UK airports",
        "image": "https://airportparkingdeals.co.uk/images/meet-greet.jpg",
        "priceRange": "££-£££",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "GB"
        },
        "telephone": "+44-800-000-0000",
        "url": "https://airportparkingdeals.co.uk/meet-and-greet-parking"
      }
    ]
  };

  return (
    <script 
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} 
    />
  );
};

export default MeetAndGreetSchema;

